import type React from "react"
import { NavigationLink } from "@crm-src/components/navigation-link"
// next/image replaced with plain <img> below; kept import line removed.
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import { useAuth } from "@crm-src/hooks/useAuth"
import { useAppSelector } from "@crm-src/lib/store"
import { useTenantStats } from "@crm-src/hooks/useTenantStats"
import { useTasks } from "@crm-src/hooks/useTasks"
import usePermissions from "@crm-src/hooks/usePermissions"
import { Box, Chip, Divider, IconButton } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { NotificationsDropdown } from "@crm-src/components/notifications-dropdown"
import { OverdueInvoicesAlert } from "@crm-src/components/overdue-invoices-alert"
import { DynamicTitle } from "@crm-src/components/dynamic-title"
import { GlobalSearch } from "@crm-src/components/global-search"
import {
  Activity,
  Users,
  UserPlus,
  CheckSquare,
  Menu,
  X,
  Target,
  User,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  FileText,
  Contact,
  FolderOpen,
  UserCog,
  Building2,
  Briefcase,
  Sparkles,
  KeyRound,
  Layers,
  Package,
  ClipboardList,
  ClipboardCheck,
} from "lucide-react"

interface CRMLayoutProps {
  children: React.ReactNode
  currentPage: string
}

export function CRMLayout({ children, currentPage }: CRMLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Every sidebar section is collapsible and opens expanded. One map, keyed
  // by section id, so a fifth section is a line in NAV_SECTIONS and nothing
  // else.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const isExpanded = (id: string) => !collapsed[id]
  const toggleSection = (id: string) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }))

  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { counts, loading: statsLoading, fetchStats } = useTenantStats({ fetchOnMount: true, autoRefresh: true })
  const { tasks, stats: taskStats } = useTasks()

  // Get user and tenant data from Redux store
  const user = useAppSelector((state) => state.auth.user)
  const tenant = useAppSelector((state) => state.auth.tenant)
  const permissions = useAppSelector((state) => state.auth.permissions)

  // Refetch sidebar count badges when the user changes (after login/logout).
  useEffect(() => {
    if (user) {
      fetchStats()
    }
  }, [user, fetchStats])

  // Task stats are now included in getTasks({ include_stats: true })
  // No need to call separate stats endpoint.
  useEffect(() => {
    // Listen for task changes — sidebar counts auto-refresh via the
    // tenantStats slice's interval, so this listener is a no-op for
    // now. Kept as a hook so tasks-page side effects continue to work
    // unchanged.
    const handleTaskChange = () => {
      // Task changes are handled by the tasks page itself.
    }

    window.addEventListener('taskCreated', handleTaskChange)
    window.addEventListener('taskUpdated', handleTaskChange)
    window.addEventListener('taskDeleted', handleTaskChange)
    window.addEventListener('taskCompleted', handleTaskChange)
    window.addEventListener('refreshTasks', handleTaskChange)

    return () => {
      window.removeEventListener('taskCreated', handleTaskChange)
      window.removeEventListener('taskUpdated', handleTaskChange)
      window.removeEventListener('taskDeleted', handleTaskChange)
      window.removeEventListener('taskCompleted', handleTaskChange)
      window.removeEventListener('refreshTasks', handleTaskChange)
    }
  }, [user])

  // Get permission functions
  const {
    canAccessTasks,
    canAccessProspects,
    canAccessCustomers,
    canAccessContacts,
    canAccessInvoices,
    canAccessFileManager,
    canAccessLicences,
    canAccessProductsPricing,
    canAccessResellers,
    canAccessFinancialMetrics,
    canAccessAdmin,
    isAdmin
  } = usePermissions()

  // Helper function to get user initials
  const getUserInitials = () => {
    if (!user) return "U"

    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    } else if (user.full_name) {
      const names = user.full_name.split(" ")
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase()
      }
      return user.full_name.charAt(0).toUpperCase()
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return "U"
  }

  // Helper function to get display name
  const getDisplayName = () => {
    if (!user) return "User"

    return user.full_name ||
           (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : "") ||
           user.email ||
           "User"
  }

  // Helper function to check if user has admin privileges
  const hasAdminAccess = () => {
    if (!user) return false

    // Check if user is active and email verified (security requirement)
    if (!user.active || !user.email_verified) return false

    // Check if user has admin role
    const hasAdminRole = user.roles && user.roles.some((role: any) =>
      role.name === 'admin' || role.name === 'Admin'
    )

    // Check if user has admin permissions
    const hasAdminPermission = permissions && permissions.some((permission: string) =>
      permission.includes('admin:') ||
      permission.includes('users:create') ||
      permission.includes('users:delete') ||
      permission.includes('roles:create') ||
      permission.includes('permissions:create')
    )

    return hasAdminRole || hasAdminPermission
  }

  // Note: Super admins now get completely different layout, so this function is no longer needed

  // Calculate task count based on user role
  // Backend now handles role-based filtering, so we can trust the stats
  const getTaskCount = () => {
    // Prefer stats.total which includes recurring tasks
    if (taskStats?.total !== undefined) {
      return taskStats.total.toString()
    }

    // Fallback to counting tasks array (backend already filtered by role)
    if (!tasks) return "0"
    return tasks.length.toString()
  }

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)

      // Call the Redux-powered logout function
      await logout()

      // Redirect will be handled by the useAuth hook or we can do it manually
      navigate("/")
    } catch (error) {
      console.error('❌ Logout failed:', error)
      // Even if logout API fails, clear local storage and redirect
      navigate("/")
    } finally {
      setIsLoggingOut(false)
    }
  }

  // Profile dropdown handlers
  const handleEditProfile = () => {
    navigate('/crm/profile')
  }




  // THE SIDEBAR IS SECTIONS (2026-09-13, CEO): what is done WITH customers,
  // what is SOLD to them, what is SHIPPED to them, and the administration of
  // the CRM itself — so the menu reads as four short lists instead of one
  // wall of thirteen entries. Every entry names its section; the sections
  // render in this order, each only when the viewer can open something in
  // it, each collapsible like Administration always was.
  //
  // Within Customers, AI Prospects sits first because `/crm` lands on
  // `/crm/ai-prospects` — it is the CRM's primary page (see
  // CrmLandingRedirect). Regular Prospects follows, then Tasks. AI Prospects
  // + Prospects share the `prospects` grant, so a user WITHOUT it skips both
  // and lands on the next page they can open (Tasks, then Customers, …).
  const NAV_SECTIONS = [
    { id: "customers", label: "Customers" },
    { id: "sales", label: "Sales" },
    { id: "products", label: "Products" },
  ] as const
  type NavSectionId = (typeof NAV_SECTIONS)[number]["id"]

  const allNavigationItems: Array<{
    name: string
    href: string
    icon: typeof Users
    badge: string | null
    permission: boolean
    section: NavSectionId
  }> = [
    // Prospects + AI Prospects each badge their OWN page's count (the two pages
    // partition the leads: regular = not_ai, AI = ai). Per-origin counts come
    // from the tenant-stats endpoint (regular_prospects / ai_prospects).
    { name: "AI Prospects", href: "/crm/ai-prospects", icon: Sparkles, badge: counts ? counts.aiProspects.toString() : "0", permission: canAccessProspects(), section: "customers" },
    { name: "Prospects", href: "/crm/prospects", icon: UserPlus, badge: counts ? counts.regularProspects.toString() : "0", permission: canAccessProspects(), section: "customers" },
    { name: "Tasks", href: "/crm/tasks", icon: CheckSquare, badge: getTaskCount(), permission: canAccessTasks(), section: "customers" },
    { name: "Customers", href: "/crm/customers", icon: Users, badge: counts?.customers ? counts.customers.toString() : "0", permission: canAccessCustomers(), section: "customers" },
    // Pre-ship Questionnaires (2026-10-03): every customer's pre-shipment form
    // in one list. Customer data, so it rides the Customers grant.
    { name: "Pre-ship Questionnaires", href: "/crm/pre-ship", icon: ClipboardCheck, badge: null, permission: canAccessCustomers(), section: "customers" },
    { name: "Resellers", href: "/crm/administration/resellers", icon: Building2, badge: counts?.resellers ? counts.resellers.toString() : "0", permission: canAccessResellers(), section: "customers" },
    // Contacts + Contact Groups are ONE destination since 2026-09-12 — two
    // tabs of `/crm/contacts` (`ContactsHubPage`), one grant.
    { name: "Contacts & Groups", href: "/crm/contacts", icon: Contact, badge: counts?.contacts ? counts.contacts.toString() : "0", permission: canAccessContacts(), section: "customers" },
    // Email Campaigns moved to the Marketing section (/marketing/campaigns) on 2026-06-02.
    { name: "Quotes", href: "/crm/quotes", icon: Briefcase, badge: null, permission: canAccessInvoices(), section: "sales" },
    { name: "Invoices", href: "/crm/invoices", icon: FileText, badge: null, permission: canAccessInvoices(), section: "sales" },
    { name: "Purchase Orders", href: "/crm/purchase-orders", icon: ClipboardList, badge: null, permission: canAccessInvoices(), section: "sales" },
    { name: "File Manager", href: "/crm/file-manager", icon: FolderOpen, badge: null, permission: canAccessFileManager(), section: "customers" },
    // Releases and Licences (2026-09-13): what a customer may INSTALL and what
    // they may RUN — two entries, one grant each. They shared an entry and a
    // tab strip for a day (2026-09-12); the CEO wanted them apart, with
    // Releases showing only the folders and a release opening into a page of
    // its own. `products_pricing` opens Releases, `licences` opens Licences.
    // "Software Releases" in all three portals (CEO, 2026-11-02); the URL is unchanged.
    { name: "Software Releases", href: "/crm/releases", icon: Layers, badge: null, permission: canAccessProductsPricing(), section: "products" },
    { name: "Licences", href: "/crm/licences", icon: KeyRound, badge: null, permission: canAccessLicences(), section: "products" },
    // Pricing (2026-09-12): the product catalogue and the XOP price list,
    // moved here out of System Admin. Sits right under Licences on purpose —
    // releases, licences and prices are the three things a customer actually
    // buys. Its own grant; super admins only by default.
    { name: "Pricing", href: "/crm/pricing", icon: Package, badge: null, permission: canAccessProductsPricing(), section: "products" },
    { name: "Financial Metrics", href: "/crm/financial-metrics", icon: Target, badge: null, permission: canAccessFinancialMetrics(), section: "sales" },
  ]

  // Only show navigation items the user has permission for — and only the
  // sections that still hold something. Quotes before Invoices under Sales:
  // that is the order the money flows. Purchase Orders follow Invoices
  // (2026-09-17): what the buyer sends back, gated like the other two.
  const navigation = allNavigationItems.filter(item => item.permission)
  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: navigation.filter((item) => item.section === section.id),
  })).filter((section) => section.items.length > 0)

  // CRM administration covers ONLY operational pages — the CRM's
  // business surfaces (audit log, API connections, resellers). Identity surfaces (accounts,
  // role definitions, tenants, cross-tenant users) are NOT here:
  // they live exclusively at /admin so there's one source of truth
  // for who can use the dashboard and what they can do. The CRM
  // reads `dashboard_role` from the same JWT the rest of the
  // dashboard uses.
  //
  // Tenant Settings and Portal Access Queue were DISCONTINUED on
  // 2026-09-12 (pages deleted, routes redirect to the CRM landing), which
  // left the old "Integrations" sub-group with a single entry — so API
  // Connections now sits directly in Administration and the sub-heading is
  // gone. One flat list, one array; don't re-introduce a sub-group for a
  // single item.
  //
  // The old Product Manager page (`/crm/administration/product-manager`) was
  // deleted on 2026-09-13 and its URL redirects: the catalogue AND its prices
  // are managed on CRM ▸ Pricing (`/crm/pricing`, above), and a second editor
  // that still offered the retired version level could only disagree with it.
  const allGeneralAdministrationItems = [
    { name: "System Activity", href: "/crm/administration/system-activity", icon: Activity, badge: null, permission: canAccessAdmin() },
  ]

  // Only show items the user has permission for.
  const generalAdminNavigation = allGeneralAdministrationItems.filter(item => item.permission)

  // ONE link renderer for every section. The main list and Administration
  // used to carry two copies of this block, which is how the two drifted
  // apart in 2026-06 (the admin links kept a blue gradient the main nav had
  // lost).
  type NavLinkItem = Pick<(typeof allNavigationItems)[number], "name" | "href" | "icon" | "badge">
  const renderNavLink = (item: NavLinkItem) => {
    const Icon = item.icon
    const pageSegment = item.href.split('/').pop() || ''
    const isActive = currentPage === pageSegment

    return (
      <NavigationLink
        key={item.name}
        href={item.href}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          fontSize: 14,
          borderRadius: 1,
          transition: "background-color 150ms, color 150ms",
          ...(isActive
            ? {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 600,
              }
            : {
                color: "text.secondary",
                fontWeight: 500,
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }),
          // Group-hover: dim icon follows the row hover.
          "&:hover .nav-icon": { color: isActive ? "primary.main" : "text.secondary" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            className="nav-icon"
            sx={{ display: "flex", mr: 1.5, color: isActive ? "primary.main" : "text.disabled" }}
          >
            <Icon size={16} />
          </Box>
          {item.name}
        </Box>
        {item.badge && (
          <Chip
            variant="outlined"
            size="small"
            label={statsLoading ? "..." : item.badge}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              "& .MuiChip-label": { px: 0.75 },
              ...(statsLoading ? { animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } } : {}),
            }}
          />
        )}
      </NavigationLink>
    )
  }

  /** A section's collapsible heading — the one Administration always had,
   *  now shared by every section. A <button> so the design-consistency spec
   *  can find and toggle it by name. */
  const renderSectionHeader = (id: string, label: string) => (
    <Box
      component="button"
      type="button"
      aria-expanded={isExpanded(id)}
      onClick={() => toggleSection(id)}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1.5,
        py: 0.75,
        mb: 0.5,
        fontSize: 11,
        fontWeight: 600,
        color: "text.disabled",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        borderRadius: 1,
        transition: "color 150ms",
        "&:hover": { color: "text.secondary" },
      }}
    >
      <span>{label}</span>
      {isExpanded(id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    </Box>
  )

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Dynamic title component */}
      <DynamicTitle />

      {/* Mobile sidebar overlay — sits below the dashboard header
          (z-index 10 < the AppShell header's z-index 20). */}
      {sidebarOpen && (
        <Box
          sx={{ position: "fixed", inset: 0, zIndex: 10, bgcolor: "rgba(0,0,0,0.5)", display: { lg: "none" } }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — starts just below the dashboard's sticky top header
          (top: var(--app-header-height)) and runs to the bottom of
          the viewport. z-index 10 keeps it underneath the header
          (z-index 20) so even if widths overlap, the header always wins. */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          bottom: 0,
          zIndex: 10,
          width: 288,
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRight: 1,
          borderColor: "divider",
          transition: "transform 300ms ease-in-out",
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          "@media (min-width:1200px)": { transform: "translateX(0)" },
        }}
        style={{ top: "var(--app-header-height)" }}
      >
        {/* Mobile close button (no header — shown only on mobile so the sidebar can be dismissed) */}
        <Box sx={{ display: { xs: "flex", lg: "none" }, justifyContent: "flex-end", p: 1 }}>
          <IconButton size="small" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </IconButton>
        </Box>

        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            pt: { xs: 2, lg: 2.5 },
            px: 1.5,
            pb: 3,
            "& > * + *": { mt: 0.25 },
          }}
        >
          {/* Global search (customers, prospects, resellers, contacts,
              invoices, quotes, files) heads the menu. It lived in the CRM's
              own top bar until that bar was removed on 2026-05-08
              (c00c71cf1); the component stayed imported but unmounted for
              five months. It belongs to the CRM, so it sits in the CRM's own
              chrome — not in the dashboard header every section shares. */}
          <Box sx={{ pb: 2 }} data-testid="crm-nav-search">
            <GlobalSearch />
          </Box>

          {/* The three working sections, then Administration below a rule. */}
          {sections.map((section, index) => (
            <Box key={section.id} sx={{ pt: index === 0 ? 0 : 2 }} data-testid={`crm-nav-${section.id}`}>
              {/* The same rule Administration has always sat under — every
                  section after the first is separated by one (CEO, 2026-09-13). */}
              {index > 0 && (
                <Box sx={{ px: 1.5, mb: 1 }}>
                  <Divider sx={{ borderColor: "divider" }} />
                </Box>
              )}
              {renderSectionHeader(section.id, section.label)}
              <Box
                sx={{
                  transition: "all 300ms ease-in-out",
                  overflow: "hidden",
                  maxHeight: isExpanded(section.id) ? "32rem" : 0,
                  opacity: isExpanded(section.id) ? 1 : 0,
                }}
              >
                {section.items.map(renderNavLink)}
              </Box>
            </Box>
          ))}

          {/* Administration Section - Only visible to admins */}
          {hasAdminAccess() && (
            <Box sx={{ pt: 2 }} data-testid="crm-nav-administration">
              <Box sx={{ px: 1.5, mb: 1 }}>
                <Divider sx={{ borderColor: "divider" }} />
              </Box>
              {renderSectionHeader("administration", "Administration")}
              {/* Collapsible Administration Items — one flat list. */}
              <Box
                sx={{
                  transition: "all 300ms ease-in-out",
                  overflow: "hidden",
                  maxHeight: isExpanded("administration") ? "32rem" : 0,
                  opacity: isExpanded("administration") ? 1 : 0,
                }}
              >
                {generalAdminNavigation.map(renderNavLink)}
              </Box>
            </Box>
          )}

          {/* No CRM-side identity management. Account, role, tenant,
              and cross-tenant user surfaces all live at /admin —
              the dashboard's System Admin is the single source of
              truth for who can use the CRM and what they can do. */}
        </Box>



      </Box>

      {/* Main content (no top bar — dashboard chrome handles top-level UI) */}
      <Box sx={{ pl: { lg: "288px" } }}>
        {/* Mobile-only floating menu button — positioned just below the
            dashboard's sticky header so it doesn't overlap. z-index 10 keeps
            it under the header (z-index 20). */}
        <IconButton
          size="small"
          sx={{
            display: { xs: "inline-flex", lg: "none" },
            position: "fixed",
            left: 8,
            zIndex: 10,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
            backdropFilter: "blur(4px)",
            boxShadow: 1,
          }}
          style={{ top: "calc(var(--app-header-height) + 0.5rem)" }}
          onClick={() => setSidebarOpen(true)}
        >
          <Menu size={20} />
        </IconButton>

        {/* Page content — inner-content padding cut to a THIRD of the
            prior values (pt 1.5→0.5, pb 4→4/3, px {2,3,4}→{2/3,1,4/3}) so
            CRM pages use the full width with only a slim gutter. This is
            the CRM-scoped *inner* content padding only — the shared
            AppShell chrome padding (`<main>` px/py in AppShell.tsx) is
            deliberately left alone so other sections are unaffected.

            The nested selectors trim the *inner container* density one more
            step, scoped to CRM only (this wrapper) — the card body padding
            (`CardContent` 16/24px → 12px) and the horizontal cell padding
            (theme's 16px → 12px) — so wide tables (Prospects/Customers/
            Resellers) reclaim ~8px/column. That extra room, together with the
            per-page `whiteSpace: nowrap` on the Code cells and ellipsis on the
            Company/Reseller name, keeps the Code column on one line. Only
            horizontal cell padding is trimmed; the vertical (row-height)
            rhythm from the theme is left untouched. */}
        <Box component="main" sx={{ pt: 0.5, pb: 4 / 3 }}>
          <Box
            data-testid="crm-content"
            sx={{
              px: { xs: 2 / 3, sm: 1, lg: 4 / 3 },
              "& .MuiCardContent-root": { p: 1.5, "&:last-child": { pb: 1.5 } },
              "& .MuiTableCell-root": { px: 1.5 },
            }}
          >
            {/* The CRM-wide overdue-invoice warning. Mounted here — above every
                page's own content — so it is impossible to miss whichever CRM
                page you are on. It renders nothing when nothing is overdue, and
                nothing at all for a viewer without invoice + financial access.
                See components/overdue-invoices-alert.tsx. */}
            <OverdueInvoicesAlert />
            {children}
          </Box>
        </Box>
      </Box>


    </Box>
  )
}
